/**
 * Vitrine croisée — « Mes autres jeux » (demande de Benoit du 18 août 2026).
 *
 * Un encart sous Progression qui renvoie vers les autres jeux de Benoit. Les entrées
 * ci-dessous sont des EXEMPLES, à remplacer par les vrais jeux au fur et à mesure :
 * une entrée = un objet, rien d'autre à toucher.
 *
 * Les NOMS sont des noms propres — ils ne se traduisent pas et vivent ici, comme les
 * mentions de crédits. Les accroches, elles, passent par `t()` (règle 9) : une clé
 * par jeu, dans les deux dictionnaires.
 *
 * `emoji` plutôt qu'une icône du jeu tant qu'il n'y a pas de vraie capture : un
 * pictogramme neutre vaut mieux qu'une image d'un autre univers graphique (E§7).
 */

import type { TranslationKey } from '@/i18n';

export interface PromoEntry {
  readonly id: string;
  /** Nom propre du jeu — jamais traduit. */
  readonly name: string;
  readonly taglineKey: TranslationKey;
  /** Adresse publique du jeu. `#` tant que le jeu n'est pas en ligne. */
  readonly url: string;
  readonly emoji: string;
  /**
   * Icône du jeu, servie depuis `public/promo/`. Facultative : l'emoji reste le repli,
   * et c'est lui qui s'affiche tant qu'un jeu n'a pas d'art à montrer.
   *
   * ⚠️ Une icône seulement, jamais une capture d'écran. E§7 met en garde contre
   * l'import d'un autre univers graphique dans celui-ci : une vignette ronde et plate
   * cohabite avec la direction artistique, une copie d'écran d'un autre jeu non.
   */
  readonly icon?: string;
}

/**
 * Les autres jeux de Benoit. Une entrée = un objet, rien d'autre à toucher.
 *
 * La liste est restée VIDE du 18 au 19 août — « mets aucun pour le moment » — et la
 * section affichait son état d'attente plutôt que des cartes d'exemple qui ressemblaient
 * à de vrais jeux. Tradopia est la première entrée réelle.
 */
export const PROMO_GAMES: readonly PromoEntry[] = [
  {
    id: 'tradopia',
    name: 'Tradopia',
    taglineKey: 'promo.tradopia',
    url: 'https://tradopia.app',
    emoji: '📈',
    icon: 'promo/tradopia.svg',
  },
  /*
    World Blocks. Le nom, l'accroche et l'icône viennent du SITE lui-même — manifeste PWA
    pour l'icône (`npm run fetch:promo-icon`), et sa propre description pour l'accroche :
    « Puzzle de blocs, construction de villes et défis entre amis. » Inventer une accroche
    pour le jeu de quelqu'un d'autre, c'est en donner une idée fausse au premier clic.

    L'icône a été redimensionnée de 512 à 96 px — 457 Ko d'origine pour une vignette
    affichée à 40, sur la page d'un jeu qui promet de démarrer en moins de cinq secondes.
  */
  {
    id: 'worldblocks',
    name: 'World Blocks',
    taglineKey: 'promo.worldblocks',
    url: 'https://worldblocks.app',
    emoji: '🧩',
    icon: 'promo/worldblocks.png',
  },
];
